import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Button, Spin, Popconfirm, message } from 'antd';
import Card from '../../../../components/Card/Card';
import Modal from '../../../../components/Modal/Modal';
import Table from '../../../../components/Table/Table';
import BrokerEdit from './components/BrokerEdit';
import { deleteBroker, resendBrokerInvitation } from './BrokerActions';
import { brokersSelector, brokerIdSelector, loadingBrokersSelector } from './BrokerSelectors';
import { MSG_ERROR } from '../../../../constants';
import { getApiErrorMessage } from '../../../../utils/httpError';

function getBrokerEmailConfirmed(record) {
  return record?.isEmailConfirmed ?? record?.IsEmailConfirmed;
}

function isBrokerInvitePending(record) {
  const value = getBrokerEmailConfirmed(record);
  return value === false || value === 0 || value === '0';
}

function isBrokerInviteAccepted(record) {
  const value = getBrokerEmailConfirmed(record);
  return value === true || value === 1 || value === '1';
}

const Broker = ({ brokers, brokerId, deleteBroker, resendBrokerInvitation, loadingBrokers }) => {
  const [resendingBrokerId, setResendingBrokerId] = useState(null);

  const onDelete = async (broker) => {
    const { brokerId: id } = broker;
    try {
      await deleteBroker(id);
      message.success('The broker has been successfully deleted');
    } catch (err) {
      message.error(getApiErrorMessage(err, MSG_ERROR));
    }
  };

  const onResend = async (broker) => {
    setResendingBrokerId(broker.brokerId);
    try {
      await resendBrokerInvitation(broker);
      message.success('Invitation resent successfully.');
    } catch (err) {
      message.error(getApiErrorMessage(err, MSG_ERROR));
    } finally {
      setResendingBrokerId(null);
    }
  };

  const columns = [
    {
      key: 'email',
      title: 'Email',
      dataIndex: 'email',
      render: (text) => (text ? <a href={`mailto:${text}`}>{text}</a> : null),
    },
    { key: 'firstName', title: 'First Name', dataIndex: 'firstName' },
    { key: 'lastName', title: 'Last Name', dataIndex: 'lastName' },
    {
      key: 'isEmailConfirmed',
      title: 'Invite status',
      render: (_, record) => {
        if (isBrokerInviteAccepted(record)) {
          return 'Invite Accepted';
        }
        if (isBrokerInvitePending(record)) {
          return '—';
        }
        return '—';
      },
    },
    {
      key: 'action',
      title: 'Actions',
      width: 120,
      render: (_, record) => {
        if (brokerId === record.brokerId) return null;

        return (
          <div className="broker-admin-actions">
            {isBrokerInvitePending(record) ? (
              <Button
                type="link"
                size="small"
                loading={resendingBrokerId === record.brokerId}
                disabled={loadingBrokers}
                onClick={() => onResend(record)}
              >
                Resend
              </Button>
            ) : null}
            <Popconfirm title="Sure to delete?" onConfirm={() => onDelete(record)}>
              <Button type="link" icon="delete" disabled={loadingBrokers} />
            </Popconfirm>
          </div>
        );
      },
    },
  ];

  return (
    <div className="component-wrapper">
      <Spin spinning={loadingBrokers}>
        <Card title="My Admin Users">
          <Table
            title="My Admin Users"
            columns={columns}
            dataSource={brokers}
            rowKey="brokerId"
            searchFields={['email', 'firstName', 'lastName']}
          />
          <div className="btn-container">
            <Modal
              toggle={(show) => (
                <Button type="primary" htmlType="submit" onClick={show}>
                  Add new
                </Button>
              )}
            >
              {(hide) => <BrokerEdit close={hide} />}
            </Modal>
          </div>
        </Card>
      </Spin>
    </div>
  );
};

const mapStateToProps = (state) => ({
  brokerId: brokerIdSelector(state),
  brokers: brokersSelector(state),
  loadingBrokers: loadingBrokersSelector(state),
});

const mapDispatchToProps = { deleteBroker, resendBrokerInvitation };

export default connect(mapStateToProps, mapDispatchToProps)(Broker);
